/**
 * Prodikt Design System Theme
 * CSS Custom Properties references
 * Import './variables.css' to use these values
 */

// Helper function to create CSS variable reference
const cssVar = (name: string) => `var(--${name})`;

export const colors = {
  // Primary Prodikt green colors
  primary: {
    50: cssVar('color-primary-50'),
    100: cssVar('color-primary-100'),
    200: cssVar('color-primary-200'),
    300: cssVar('color-primary-300'),
    400: cssVar('color-primary-400'),
    500: cssVar('color-primary-500'),  // Main Prodikt green
    600: cssVar('color-primary-600'),
    700: cssVar('color-primary-700'),
    800: cssVar('color-primary-800'),
    900: cssVar('color-primary-900'),
  },

  // Accent colors
  accent: {
    border: cssVar('color-accent-border'),
    bg: cssVar('color-accent-bg'),
    primary: cssVar('color-accent-primary'),
    hover: cssVar('color-accent-hover'),
  },

  // Neutral/Gray colors
  gray: {
    50: cssVar('color-gray-50'),
    100: cssVar('color-gray-100'),
    200: cssVar('color-gray-200'),
    300: cssVar('color-gray-300'),
    400: cssVar('color-gray-400'),
    500: cssVar('color-gray-500'),
    600: cssVar('color-gray-600'),
    700: cssVar('color-gray-700'),
    800: cssVar('color-gray-800'),
    900: cssVar('color-gray-900'),
  },

  // Semantic colors
  success: {
    light: cssVar('color-success-light'),
    main: cssVar('color-success-main'),
    dark: cssVar('color-success-dark'),
  },

  error: {
    light: cssVar('color-error-light'),
    main: cssVar('color-error-main'),
    dark: cssVar('color-error-dark'),
  },

  warning: {
    light: cssVar('color-warning-light'),
    main: cssVar('color-warning-main'),
    dark: cssVar('color-warning-dark'),
  },

  info: {
    light: cssVar('color-info-light'),
    main: cssVar('color-info-main'),
    dark: cssVar('color-info-dark'),
  },

  // Background colors
  background: {
    primary: cssVar('color-background-primary'),
    secondary: cssVar('color-background-secondary'),
    tertiary: cssVar('color-background-tertiary'),
    dark: cssVar('color-background-dark'),
    darker: cssVar('color-background-darker'),
  },

  // Text colors
  text: {
    primary: cssVar('color-text-primary'),
    secondary: cssVar('color-text-secondary'),
    tertiary: cssVar('color-text-tertiary'),
    detailValue: cssVar('color-text-detail-value'),
    disabled: cssVar('color-text-disabled'),
    placeholder: cssVar('color-text-placeholder'),
    inverse: cssVar('color-text-inverse'),
  },

  // Label colors
  label: {
    bg: cssVar('color-label-bg'),
    text: cssVar('color-label-text'),
    border: cssVar('color-label-border'),
  },

  // Border colors
  border: {
    light: cssVar('color-border-light'),
    main: cssVar('color-border-main'),
    dark: cssVar('color-border-dark'),
  },
} as const;

export const typography = {
  // Font families
  fontFamily: {
    primary: cssVar('font-family-primary'),
    bold: cssVar('font-family-bold'),
    mono: cssVar('font-family-mono'),
  },

  // Font sizes
  fontSize: {
    xs: cssVar('font-size-xs'),
    sm: cssVar('font-size-sm'),
    base: cssVar('font-size-base'),
    md: cssVar('font-size-md'),
    lg: cssVar('font-size-lg'),
    xl: cssVar('font-size-xl'),
    '2xl': cssVar('font-size-2xl'),
    '3xl': cssVar('font-size-3xl'),
    '4xl': cssVar('font-size-4xl'),
  },

  // Font weights
  fontWeight: {
    light: cssVar('font-weight-light'),
    normal: cssVar('font-weight-normal'),
    medium: cssVar('font-weight-medium'),
    semibold: cssVar('font-weight-semibold'),
    bold: cssVar('font-weight-bold'),
  },

  // Line heights
  lineHeight: {
    tight: cssVar('line-height-tight'),
    normal: cssVar('line-height-normal'),
    relaxed: cssVar('line-height-relaxed'),
  },
} as const;

export const spacing = {
  0: cssVar('spacing-0'),
  1: cssVar('spacing-1'),
  2: cssVar('spacing-2'),
  3: cssVar('spacing-3'),
  4: cssVar('spacing-4'),
  5: cssVar('spacing-5'),
  6: cssVar('spacing-6'),
  8: cssVar('spacing-8'),
  10: cssVar('spacing-10'),
  12: cssVar('spacing-12'),
  16: cssVar('spacing-16'),
  20: cssVar('spacing-20'),
  24: cssVar('spacing-24'),
} as const;

export const borderRadius = {
  none: cssVar('border-radius-none'),
  sm: cssVar('border-radius-sm'),
  md: cssVar('border-radius-md'),
  lg: cssVar('border-radius-lg'),
  xl: cssVar('border-radius-xl'),
  xxl: cssVar('border-radius-xxl'),
  xxxl: cssVar('border-radius-xxxl'),
  full: cssVar('border-radius-full'),
} as const;

export const shadows = {
  none: cssVar('shadow-none'),
  sm: cssVar('shadow-sm'),
  md: cssVar('shadow-md'),
  lg: cssVar('shadow-lg'),
  xl: cssVar('shadow-xl'),
  card: cssVar('shadow-card'),
  cardHover: cssVar('shadow-card-hover'),
  projectCard: cssVar('shadow-project-card'),
  projectCardHover: cssVar('shadow-project-card-hover'),
  imageInset: cssVar('shadow-image-inset'),
  popover: cssVar('shadow-popover'),
  dropdown: cssVar('shadow-dropdown'),
  input: cssVar('shadow-input'),
} as const;

export const transitions = {
  fast: cssVar('transition-fast'),
  normal: cssVar('transition-normal'),
  slow: cssVar('transition-slow'),
  smooth: cssVar('transition-smooth'),
} as const;

export const easings = {
  default: cssVar('easing-default'),
} as const;

// Default theme export
export const theme = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  easings,
} as const;

export default theme;
